/**
 * Service Worker Update Utility
 *
 * Checks for service worker updates and notifies the app when updates are available
 * BUG FIX v17: Enhanced update detection
 * BUG FIX v17 (BUG 5): Shows user-friendly prompt instead of auto-reloading
 * BUG FIX P0: Removed auto-reload on controllerchange to prevent reload loops
 * BUG FIX P0: Reduced update frequency and added guards for unregistered SW
 * BUG FIX: Single-prompt guard — callback fires at most once per pending update;
 *           resets only when the user actions the update via applyUpdate()
 * BUG FIX: controllerchange-driven reload — applyUpdate() now waits for the browser
 *           signal that the new worker has taken control before navigating, removing
 *           reliance on arbitrary timing delays (fixes prompt reappearing on iOS PWA)
 * BUG FIX: didNavigate one-shot guard in postSkipWaiting prevents double navigation
 *           if controllerchange and the 3s safety timeout race each other
 */

let updateCheckInProgress = false;
let updateListenerAttached = false;
let updateAvailableCallback: ((registration: ServiceWorkerRegistration) => void) | null = null;
let waitingServiceWorker: ServiceWorker | null = null;
// BUG FIX P0: Track if cleanup has run — if so, skip update checks
let cleanupHasRun = false;
// Single-prompt guard: prevents callback from firing more than once while a
// prompt is already visible. Reset only as part of the successful update flow
// or on explicit dismiss.
let updateAlreadyNotified = false;

/**
 * BUG FIX P0: Called by cleanup to signal that update checks should stop
 */
export function disableUpdateChecks(): void {
  cleanupHasRun = true;
}

/**
 * Internal helper — fires the callback at most once until the update is actioned.
 */
function notifyUpdateAvailable(registration: ServiceWorkerRegistration): void {
  if (updateAlreadyNotified) {
    console.log('[SWUpdate] Update already notified, suppressing duplicate prompt');
    return;
  }
  updateAlreadyNotified = true;
  if (updateAvailableCallback) {
    updateAvailableCallback(registration);
  }
}

/**
 * Reset the notification guard. Called only after successful update handoff
 * or on explicit dismiss. Not called prematurely before controllerchange.
 */
export function resetUpdateNotification(): void {
  updateAlreadyNotified = false;
}

/**
 * Check for service worker updates and apply them automatically
 * BUG FIX v17: Enhanced to detect updates immediately and reload without user intervention
 * BUG FIX P0: Added guards for missing/unregistered service workers
 */
export async function checkAndApplyServiceWorkerUpdate(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  // BUG FIX P0: Don't run update checks if cleanup has unregistered the SW
  if (cleanupHasRun) {
    return false;
  }

  // Prevent multiple simultaneous update checks
  if (updateCheckInProgress) {
    return false;
  }

  try {
    updateCheckInProgress = true;

    // BUG FIX P0: Guard — check registrations exist before proceeding
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) {
      console.log('[SWUpdate] No service worker registration found, skipping update check');
      updateCheckInProgress = false;
      return false;
    }

    let registration: ServiceWorkerRegistration;
    try {
      registration = await navigator.serviceWorker.ready;
    } catch {
      console.log('[SWUpdate] Service worker not ready, skipping update check');
      updateCheckInProgress = false;
      return false;
    }

    // BUG FIX P0: Guard — if registration has no active worker, skip
    if (!registration.active) {
      console.log('[SWUpdate] No active service worker, skipping update check');
      updateCheckInProgress = false;
      return false;
    }

    // BUG FIX v17 (BUG 5): Attach update listener only once to avoid duplicates
    if (!updateListenerAttached) {
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing || registration.waiting;
        if (!newWorker) return;

        console.log('[SWUpdate] New service worker found, state:', newWorker.state);

        const handleStateChange = () => {
          console.log('[SWUpdate] Service worker state changed:', newWorker.state);

          if (newWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log('[SWUpdate] BUG FIX v17 (BUG 5): New service worker installed, notifying app...');
              waitingServiceWorker = newWorker;
              notifyUpdateAvailable(registration);
            } else {
              console.log('[SWUpdate] Service worker installed (no controller — first install or post-cleanup)');
              if (document.readyState === 'complete') {
                console.log('[SWUpdate] Page already loaded, treating as update — notifying app...');
                waitingServiceWorker = newWorker;
                notifyUpdateAvailable(registration);
              }
            }
          } else if (newWorker.state === 'activated') {
            console.log('[SWUpdate] New service worker activated');
          }
        };

        newWorker.addEventListener('statechange', handleStateChange);
        handleStateChange();
      });
      updateListenerAttached = true;
    }

    // iOS fix: force a network revalidation before update() to nudge iOS into fetching the SW
    try {
      await fetch('/sw.js', { cache: 'no-store' });
    } catch {
      // Silently ignore — offline or network error, proceed anyway
    }

    // BUG FIX P0: Wrap update() in try-catch — it throws if registration is stale
    try {
      await registration.update();
    } catch (error) {
      console.warn('[SWUpdate] registration.update() failed (SW may have been unregistered):', error);
      updateCheckInProgress = false;
      return false;
    }

    // BUG FIX v17 (BUG 5): Check if there's a waiting service worker and notify app
    if (registration.waiting) {
      console.log('[SWUpdate] BUG FIX v17 (BUG 5): Waiting service worker found, notifying app...');
      waitingServiceWorker = registration.waiting;
      notifyUpdateAvailable(registration);
    }

    updateCheckInProgress = false;
    return true;
  } catch (error) {
    console.error('[SWUpdate] Failed to check for updates:', error);
    updateCheckInProgress = false;
    return false;
  }
}

/**
 * Set up periodic update checks + foreground detection
 * Checks on load, every 5 minutes, and when app returns to foreground
 */
export function setupPeriodicUpdateCheck(): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  // Detect iOS home screen PWA — needs longer delay for network reconnect
  const isIOSPWA = typeof window !== 'undefined' && (navigator as any).standalone === true;

  // Check once on load (after a short delay to let app settle)
  setTimeout(() => {
    checkAndApplyServiceWorkerUpdate();
  }, 5000);

  // Add update probe on app load — most reliable moment on iOS
  window.addEventListener('load', () => {
    setTimeout(() => {
      navigator.serviceWorker.getRegistration().then(reg => {
        if (reg) reg.update().catch(() => { /* silently ignore */ });
      }).catch(() => { /* silently ignore */ });
    }, 3000);
  });

  // Check every 5 minutes (reasonable for home-screen PWA users)
  setInterval(() => {
    if (!cleanupHasRun) {
      checkAndApplyServiceWorkerUpdate();
    }
  }, 5 * 60 * 1000);

  // Check when app returns to foreground — longer delay on iOS PWA
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !cleanupHasRun) {
      const delay = isIOSPWA ? 4000 : 2000;
      setTimeout(() => {
        checkAndApplyServiceWorkerUpdate();
      }, delay);
    }
  });
}

/**
 * Listen for service worker messages (for skip waiting)
 * BUG FIX P0: REMOVED auto-reload on controllerchange — this was causing the reload loop
 * BUG FIX P0: REMOVED update checks on every navigation — too aggressive
 */
export function setupServiceWorkerMessageListener(): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  // Note: controllerchange is intentionally not used here for auto-reload.
  // Navigation after update is handled exclusively inside applyUpdate() where
  // a one-time controllerchange listener is registered only when the user
  // explicitly taps Refresh.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.log('[SWUpdate] Service worker controller changed.');
  });
}

/**
 * BUG FIX v17 (BUG 5): Register callback for when update is available
 * This allows the app to show a user-friendly prompt instead of auto-reloading
 */
export function onUpdateAvailable(callback: (registration: ServiceWorkerRegistration) => void): void {
  updateAvailableCallback = callback;

  // If there's already a waiting service worker, notify immediately
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) {
        waitingServiceWorker = registration.waiting;
        notifyUpdateAvailable(registration);
      }
    }).catch(() => {
      // Service worker not ready yet, ignore
    });
  }
}

/**
 * Apply the pending update.
 * Called when the user taps Refresh in the update prompt.
 *
 * Flow:
 *   1. Register a one-time controllerchange listener — this is the browser's
 *      signal that the new worker has actually taken control of this page.
 *      clients.claim() is confirmed present in vite.config.ts workbox config,
 *      so this event will fire for already-open clients after skipWaiting().
 *   2. Post SKIP_WAITING to the waiting worker to promote it.
 *   3. On controllerchange, reset the notification guard and navigate.
 *      Guard is reset here — not before — so a failed handoff cannot leave
 *      the session in a state where the prompt is permanently suppressed.
 *   4. Safety timeout: if controllerchange does not fire within 3 seconds,
 *      navigate anyway.
 *   5. didNavigate one-shot boolean ensures controllerchange and the safety
 *      timeout cannot both trigger navigation if they race each other.
 */
export async function applyUpdate(): Promise<void> {
  const postSkipWaiting = (worker: ServiceWorker) => {
    console.log('[SWUpdate] Posting SKIP_WAITING...');

    // One-shot guard: ensures only one navigation occurs regardless of whether
    // controllerchange and the safety timeout fire in the same event loop tick.
    let didNavigate = false;

    const doNavigate = (reason: string) => {
      if (didNavigate) {
        console.log(`[SWUpdate] Navigation already in progress, ignoring duplicate trigger (${reason})`);
        return;
      }
      didNavigate = true;
      console.log(`[SWUpdate] Navigating to apply update (${reason})...`);
      resetUpdateNotification();
      window.location.href = window.location.href;
    };

    // One-time controllerchange listener — fires when the new worker takes control
    const onControllerChange = () => {
      clearTimeout(safetyTimer);
      doNavigate('controllerchange');
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true });

    // Safety timeout in case controllerchange does not fire within 3 seconds
    const safetyTimer = setTimeout(() => {
      console.warn('[SWUpdate] controllerchange did not fire within 3s, navigating anyway...');
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      doNavigate('safety-timeout');
    }, 3000);

    worker.postMessage({ type: 'SKIP_WAITING' });
  };

  if (waitingServiceWorker) {
    postSkipWaiting(waitingServiceWorker);
    return;
  }

  // Fallback: check for waiting worker if module-level reference was lost
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.waiting) {
        waitingServiceWorker = registration.waiting;
        postSkipWaiting(waitingServiceWorker);
      } else {
        console.warn('[SWUpdate] applyUpdate called but no waiting worker found');
      }
    } catch (error) {
      console.error('[SWUpdate] Failed to apply update:', error);
    }
  }
}
